// src/components/Quotes/QuoteList.jsx
import React, { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FaEdit, FaTrash, FaCopy, FaFilePdf, FaChevronDown, FaThList, FaThLarge, FaTruckLoading } from "react-icons/fa";
import EmptyState from "../common/EmptyState";
import { toast } from "react-toastify";
import api from "../../api";

const ALL_STATUSES = ["nouveau", "pending", "validated", "canceled", "follow-up", "refused"];

const STATUS_TRANSITIONS = Object.fromEntries(
  ALL_STATUSES.map((s) => [s, ALL_STATUSES.filter((t) => t !== s)])
);

const QuoteList = ({
  quotes,
  setQuotes,
  handleEditClick,
  handleDelete,
  handleChangeStatus,
  onCopy,
  onViewDetail,
  filters,
  onFilterChange,
  page,
  pageCount,
  total,
  onPageChange,
}) => {
  const { t } = useTranslation();

  const [copyingId, setCopyingId] = useState(null);
  const [loadingEditId, setLoadingEditId] = useState(null);
  const [pdfMenuId, setPdfMenuId] = useState(null);

  // Vue table (par défaut) ou cartes — choix persisté, changeable à tout moment.
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem("quoteListView") || "table"; } catch { return "table"; }
  });
  useEffect(() => {
    try { localStorage.setItem("quoteListView", viewMode); } catch {}
  }, [viewMode]);

  // Résolution id sous-traitant -> nom, pour l'affichage (badge cartes + tableau).
  const [subcontractors, setSubcontractors] = useState([]);
  useEffect(() => {
    api.get("/subcontractors").then((r) => setSubcontractors(r.data || [])).catch(() => {});
  }, []);
  const subcontractorName = (id) => subcontractors.find((s) => s.id === id)?.name;

  // ---- helpers ----
  const parseIfJson = (val, fallback) => {
    if (val == null) return fallback;
    if (typeof val === "object") return val;
    try { return JSON.parse(val); } catch { return fallback; }
  };

  const normalizeForForm = (q) => ({
    ...q,
    prestations: parseIfJson(q.prestations, []),
    logistics: parseIfJson(q.logistics, {}),
    calculationData: parseIfJson(q.calculationData, undefined),
    inventoryItems: parseIfJson(q.inventoryItems, undefined),
  });

  // Prestataire de la 1re prestation (ligne SERVICE) — internal par défaut.
  const getProvider = (q) => {
    const prestations = parseIfJson(q.prestations, []);
    const svc = Array.isArray(prestations)
      ? (prestations.find((p) => p?.__fixed === "SERVICE") || prestations[0])
      : null;
    return svc?.provider_type === "subcontractor"
      ? { type: "subcontractor", subcontractorId: svc.subcontractor_id }
      : { type: "internal", subcontractorId: null };
  };

  const buildInventoryItems = (q) => {
    let items = parseIfJson(q.inventoryItems, null);
    if (!Array.isArray(items) || items.length === 0) {
      const inv = parseIfJson(q.inventory, []);
      if (Array.isArray(inv) && inv.length) {
        items = inv.map((it) => ({
          inventory_item_id: Number(it.inventory_item_id ?? it.itemId ?? it.id ?? 0) || 0,
          quantity: Math.max(1, Number(it.quantity) || 1),
        }));
      }
    } else {
      items = items.map((it) => ({
        inventory_item_id: Number(it.inventory_item_id ?? it.itemId ?? it.id ?? 0) || 0,
        quantity: Math.max(1, Number(it.quantity) || 1),
      }));
    }
    return Array.isArray(items) ? items.filter((x) => x.inventory_item_id > 0) : [];
  };

  const fmtDateShort = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const main = d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" });
    const wd = d.toLocaleDateString("fr-FR", { weekday: "short" });
    return `${main} (${wd.replace(".", "")})`;
  };

  // validated_at vient de SQLite (datetime('now') en UTC, sans suffixe TZ) → on force 'Z'
  const fmtDateTime = (val) => {
    if (!val) return "";
    const iso = val.includes("T") ? val : val.replace(" ", "T") + "Z";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString("fr-FR", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  };

  // Close menus on outside click
  useEffect(() => {
    const closeMenus = () => {
      setQuotes((prev) => prev.map((q) => ({ ...q, showStatusMenu: false })));
      setPdfMenuId(null);
    };
    window.addEventListener("click", closeMenus);
    return () => window.removeEventListener("click", closeMenus);
  }, [setQuotes]);

  const toggleStatusMenu = (quoteId) => {
    setQuotes((prev) =>
      prev.map((q) =>
        q.id === quoteId ? { ...q, showStatusMenu: !q.showStatusMenu } : { ...q, showStatusMenu: false }
      )
    );
    setPdfMenuId(null);
  };

  const togglePdfMenu = (quoteId, e) => {
    e.stopPropagation();
    setPdfMenuId((prev) => (prev === quoteId ? null : quoteId));
    setQuotes((prev) => prev.map((q) => ({ ...q, showStatusMenu: false })));
  };

  // ---- PDF download ----
  const downloadPDF = async (id, mode) => {
    try {
      const res = await api.get(`/quote/generate/${id}`, {
        responseType: "blob",
        params: mode === "ht" ? { ht: "1" } : undefined,
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const fileURL = URL.createObjectURL(blob);
      window.open(fileURL, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error("PDF download failed:", err);
      toast.error(t("quote.pdfError") || "Erreur lors du téléchargement du PDF.");
    }
  };

  // ---- copy ----
  const handleCopy = async (row, e) => {
    e?.stopPropagation?.();
    if (!row?.id) return;
    try {
      setCopyingId(row.id);
      const { data: qRaw } = await api.get(`/quote/${row.id}`);
      const inventoryItems = buildInventoryItems(qRaw);
      await onCopy({
        ...qRaw,
        prestations: parseIfJson(qRaw.prestations, []),
        logistics: parseIfJson(qRaw.logistics, {}),
        calculationData: parseIfJson(qRaw.calculationData, {}),
        inventoryItems,
      });
    } catch (err) {
      console.error("COPY quote failed:", err);
      toast.error(t("quote.createFail", "Échec de la copie du devis."));
    } finally {
      setCopyingId(null);
    }
  };

  // Un lead importé (statut "nouveau") passe en "En attente" dès qu'un commercial
  // l'ouvre — il n'est alors plus "non vu". Fire-and-forget + maj optimiste locale.
  const markSeenIfNew = (row) => {
    if (row?.status !== "nouveau") return;
    setQuotes?.((prev) => (prev || []).map((q) => (q.id === row.id ? { ...q, status: "pending" } : q)));
    api.put(`/quote/${row.id}/status`, { status: "pending" }).catch(() => {});
  };

  // ---- clic sur une ligne (n'importe quel statut) : page détail ----
  // Éditer reste une action explicite (crayon), pas le clic ligne.
  const handleRowClick = (row) => {
    if (!row?.id) return;
    markSeenIfNew(row);
    onViewDetail?.(row.id);
  };

  // ---- edit ----
  const openEdit = async (row, e) => {
    e?.stopPropagation?.();
    if (!row?.id) return;
    markSeenIfNew(row);
    // Anti-fraude : un devis validé n'est pas modifiable → repasser en attente d'abord.
    if (row.status === "validated") {
      onViewDetail?.(row.id);
      return;
    }
    try {
      setLoadingEditId(row.id);
      const { data } = await api.get(`/quote/${row.id}`);
      handleEditClick(normalizeForForm(data));
    } catch (err) {
      console.error("EDIT load failed:", err);
      toast.error(t("quote.fetchError", "Erreur de chargement du devis"));
    } finally {
      setLoadingEditId(null);
    }
  };

  // ---- status badge styles ----
  const statusBadgeClass = (status) => {
    switch (status) {
      case "nouveau":   return "bg-indigo-500/15 text-indigo-600 border border-indigo-500/30 font-bold";
      case "pending":   return "bg-amber-500/10 text-amber-500 border border-amber-500/20";
      case "validated": return "bg-emerald-500/10 text-emerald-500 border border-emerald-500/20";
      case "canceled":  return "bg-rose-500/10 text-rose-500 border border-rose-500/20";
      case "follow-up": return "bg-sky-500/10 text-sky-500 border border-sky-500/20";
      case "refused":   return "bg-violet-500/10 text-violet-500 border border-violet-500/20";
      default:          return "bg-base-300 text-base-content/50 border border-base-300";
    }
  };

  // Pagination display: show at most 7 page buttons
  const pageButtons = useMemo(() => {
    if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i + 1);
    const around = new Set([1, pageCount, page, page - 1, page + 1].filter(p => p >= 1 && p <= pageCount));
    return [...around].sort((a, b) => a - b);
  }, [page, pageCount]);

  return (
    <div className="space-y-4">
      {/* Filtres */}
      <div className="bg-base-100 rounded-xl border border-base-200 shadow-sm p-4 space-y-3">
        {/* Statuts en pills */}
        <div className="flex flex-wrap gap-2">
          {[
            { value: "",          label: "Tous",      cls: "bg-base-200 text-base-content/70 border-base-300 hover:bg-base-300" },
            { value: "nouveau",   label: t("quote.statusOptions.nouveau",   "Nouveau"),    cls: "bg-indigo-500/15 text-indigo-600 border-indigo-500/40 hover:bg-indigo-500/25 font-bold" },
            { value: "pending",   label: t("quote.statusOptions.pending",   "En attente"), cls: "bg-amber-500/10 text-amber-600 border-amber-500/30 hover:bg-amber-500/20" },
            { value: "follow-up", label: t("quote.statusOptions.follow-up", "Suivi"),      cls: "bg-sky-500/10 text-sky-600 border-sky-500/30 hover:bg-sky-500/20" },
            { value: "validated", label: t("quote.statusOptions.validated", "Validé"),     cls: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30 hover:bg-emerald-500/20" },
            { value: "refused",   label: t("quote.statusOptions.refused",   "Refusé"),     cls: "bg-violet-500/10 text-violet-600 border-violet-500/30 hover:bg-violet-500/20" },
            { value: "canceled",  label: t("quote.statusOptions.canceled",  "Annulé"),     cls: "bg-rose-500/10 text-rose-600 border-rose-500/30 hover:bg-rose-500/20" },
          ].map(({ value, label, cls }) => (
            <button
              key={value}
              onClick={() => onFilterChange({ ...filters, status: value })}
              className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${cls} ${
                filters.status === value ? "ring-2 ring-offset-1 ring-current" : ""
              }`}
            >
              {label}
            </button>
          ))}
          {/* Filtre sous-traités (indépendant du statut) */}
          <button
            onClick={() => onFilterChange({ ...filters, provider: filters.provider === "subcontractor" ? "" : "subcontractor" })}
            className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border transition-colors bg-orange-500/10 text-orange-600 border-orange-500/30 hover:bg-orange-500/20 ${
              filters.provider === "subcontractor" ? "ring-2 ring-offset-1 ring-current" : ""
            }`}
          >
            <FaTruckLoading size={10} /> {t("quote.filterSubcontracted", "Sous-traités")}
          </button>
        </div>

        {/* Recherche + mois */}
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-base-content/35 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path strokeLinecap="round" d="M21 21l-4.35-4.35"/></svg>
            <input
              type="text"
              placeholder={t("quote.searchPlaceholder") || "Nom, email, ville, n° devis…"}
              value={filters.search}
              onChange={(e) => onFilterChange({ ...filters, search: e.target.value })}
              className="input input-bordered bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none min-w-[200px] h-8 text-sm pl-8"
            />
          </div>
          <input
            type="month"
            value={filters.month}
            onChange={(e) => onFilterChange({ ...filters, month: e.target.value })}
            className="input input-bordered bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none h-8 text-sm"
          />
          {(filters.search || filters.status || filters.month || filters.provider) && (
            <button
              onClick={() => onFilterChange({ search: "", status: "", month: "", provider: "" })}
              className="text-xs text-base-content/40 hover:text-base-content/70 underline transition-colors"
            >
              Réinitialiser
            </button>
          )}
          <span className="ml-auto text-xs text-base-content/40 font-medium">
            {total} devis
          </span>
          {/* Bascule vue table / cartes — changeable à tout moment */}
          <div className="flex rounded-lg overflow-hidden border border-base-300">
            <button
              onClick={() => setViewMode("table")}
              title={t("quote.viewTable", "Vue tableau")}
              className={`px-2.5 py-1.5 transition-colors ${viewMode === "table" ? "bg-primary text-primary-content" : "bg-base-100 text-base-content/50 hover:bg-base-200"}`}
            >
              <FaThList size={12} />
            </button>
            <button
              onClick={() => setViewMode("cards")}
              title={t("quote.viewCards", "Vue cartes")}
              className={`px-2.5 py-1.5 transition-colors border-l border-base-300 ${viewMode === "cards" ? "bg-primary text-primary-content" : "bg-base-100 text-base-content/50 hover:bg-base-200"}`}
            >
              <FaThLarge size={12} />
            </button>
          </div>
        </div>
      </div>

      {/* Vue cartes */}
      {viewMode === "cards" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {quotes.length === 0 ? (
            <div className="col-span-full">
              <EmptyState type="quotes" />
            </div>
          ) : quotes.map((q) => {
            const logistics = parseIfJson(q.logistics, {});
            const provider = getProvider(q);
            const totalTTC = Number(q.totalTTC || q.totalFullTTC || 0);
            const totalHT  = Number(q.totalHT  || q.totalFullHT  || 0);
            const isSub = provider.type === "subcontractor";
            return (
              <div
                key={q.id}
                onClick={() => handleRowClick(q)}
                className={`group cursor-pointer bg-base-100 rounded-2xl border shadow-sm overflow-hidden hover:shadow-lg transition-all ${
                  isSub ? "border-orange-300/60 hover:border-orange-400" : "border-base-200 hover:border-primary/40"
                }`}
              >
                {/* Bandeau prestataire */}
                <div className={`px-4 py-1.5 flex items-center justify-between text-[10px] font-bold uppercase tracking-wide ${
                  isSub ? "bg-orange-500 text-white" : "bg-base-200 text-base-content/50"
                }`}>
                  <span className="flex items-center gap-1.5">
                    {isSub ? <TruckIcon /> : <HouseIcon />}
                    {isSub
                      ? (subcontractorName(provider.subcontractorId) || t("quote.providerSubcontractor", "Sous-traité"))
                      : t("quote.providerInternal", "Équipe interne")}
                  </span>
                  <span className={`px-1.5 py-0.5 rounded-full border font-bold normal-case ${
                    isSub ? "bg-white/15 border-white/30 text-white" : statusBadgeClass(q.status)
                  }`}>
                    {t(`quote.statusOptions.${q.status}`, q.status)}
                  </span>
                </div>

                <div className="p-4 space-y-3">
                  {/* Client + tag sous-traité + date */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className="font-bold text-base-content text-sm truncate">{q.client_name}</span>
                      {isSub && (
                        <span
                          className="inline-flex items-center gap-1 shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-orange-500/10 text-orange-600 border border-orange-500/30"
                          title={subcontractorName(provider.subcontractorId) || t("quote.providerSubcontractor", "Sous-traité")}
                        >
                          <TruckIcon /> {t("quote.subcontractedTag", "Sous-traité")}
                        </span>
                      )}
                      {q.import_warning && (
                        <span
                          className="inline-flex items-center gap-1 shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/15 text-amber-600 border border-amber-500/40 cursor-help"
                          title={q.import_warning}
                        >
                          ⚠️ {t("quote.importWarningTag", "À vérifier")}
                        </span>
                      )}
                    </div>
                    <span className="flex items-center gap-1 text-[11px] text-base-content/40 shrink-0">
                      <CalendarIcon />
                      {q.date ? new Date(q.date).toLocaleDateString("fr-FR") : "—"}
                    </span>
                  </div>

                  {/* Trajet CP départ -> arrivée */}
                  <div className="flex items-center gap-2 rounded-xl bg-base-200/60 px-3 py-2">
                    <RouteIcon />
                    <span className="text-sm font-bold text-base-content tracking-wide">
                      {logistics.loading_zip || "?"}
                    </span>
                    <svg width="16" height="10" viewBox="0 0 16 10" fill="none" className="text-base-content/30 shrink-0">
                      <path d="M1 5h13M9 1l5 4-5 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span className="text-sm font-bold text-base-content tracking-wide">
                      {logistics.delivery_zip || "?"}
                    </span>
                  </div>

                  {/* Prix HT / TTC */}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="rounded-xl border border-base-200 px-3 py-2">
                      <p className="text-[9px] font-bold uppercase text-base-content/35">{t("quote.totalHT", "Total HT")}</p>
                      <p className="text-sm font-bold text-base-content">{euroShort(totalHT)}</p>
                    </div>
                    <div className="rounded-xl border border-primary/20 bg-primary/5 px-3 py-2">
                      <p className="text-[9px] font-bold uppercase text-primary/60">{t("quote.totalTTC", "Total TTC")}</p>
                      <p className="text-sm font-bold text-primary">{euroShort(totalTTC)}</p>
                    </div>
                  </div>

                  {/* Moyen de paiement */}
                  <div className="flex items-center gap-1.5 text-xs text-base-content/50">
                    <CardIcon />
                    {q.payment_method || t("quote.paymentMethodNone", "Non renseigné")}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Tableau */}
      {viewMode === "table" && (
      <div className="bg-base-100 rounded-xl border border-base-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-base-200 border-b border-base-200">
                <th className="py-3 px-4 text-left text-[11px] font-semibold tracking-wider uppercase text-base-content/50 min-w-[120px]">
                  {t("quote.date")}
                </th>
                <th className="py-3 px-4 text-left text-[11px] font-semibold tracking-wider uppercase text-base-content/50">
                  {t("quote.clientName")}
                </th>
                <th className="py-3 px-4 text-right text-[11px] font-semibold tracking-wider uppercase text-base-content/50">
                  {t("quote.totalTTC")}
                </th>
                <th className="py-3 px-4 text-center text-[11px] font-semibold tracking-wider uppercase text-base-content/50">
                  {t("quote.status")}
                </th>
                <th className="py-3 px-4 text-center text-[11px] font-semibold tracking-wider uppercase text-base-content/50">
                  {t("quote.actions")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-base-200">
              {quotes.length === 0 ? (
                <tr>
                  <td colSpan={5}>
                    <EmptyState
                      type="quotes"
                      title={filters.search || filters.status || filters.month ? "Aucun devis trouvé" : undefined}
                      description={filters.search || filters.status || filters.month ? "Essayez de modifier vos filtres." : undefined}
                    />
                  </td>
                </tr>
              ) : (
                quotes.map((q) => {
                  // Canonique totalTTC ; fallback totalFullTTC pour données legacy (|| gère aussi le 0 hérité)
                  const totalTTC = Number(q.totalTTC || q.totalFullTTC || 0);
                  const provider = getProvider(q);
                  return (
                    <tr
                      key={q.id}
                      onClick={() => handleRowClick(q)}
                      className="hover:bg-base-200/50 transition-colors group cursor-pointer"
                    >
                      {/* Date */}
                      <td className="py-3 px-4 whitespace-nowrap text-sm text-base-content/70">
                        {fmtDateShort(q.date)}
                      </td>

                      {/* Client */}
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <span
                            className="text-sm font-medium text-base-content cursor-help"
                            title={`${q.client_name}\n${q.client_address || ""}${q.client_city ? " - " + q.client_city : ""}`}
                          >
                            {q.client_name}
                          </span>
                          {provider.type === "subcontractor" && (
                            <span
                              className="inline-flex items-center gap-1 shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-orange-500/10 text-orange-600 border border-orange-500/30"
                              title={subcontractorName(provider.subcontractorId) || t("quote.providerSubcontractor", "Sous-traité")}
                            >
                              <TruckIcon />
                              {t("quote.subcontractedTag", "Sous-traité")}
                            </span>
                          )}
                          {q.import_warning && (
                            <span
                              className="inline-flex items-center gap-1 shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/15 text-amber-600 border border-amber-500/40 cursor-help"
                              title={q.import_warning}
                            >
                              ⚠️ {t("quote.importWarningTag", "À vérifier")}
                            </span>
                          )}
                        </div>
                        {q.client_city ? (
                          <div className="text-xs text-base-content/40 truncate max-w-[280px] mt-0.5">
                            {q.client_city}
                          </div>
                        ) : null}
                      </td>

                      {/* Total TTC */}
                      <td className="py-3 px-4 text-right text-sm font-semibold text-base-content">
                        {totalTTC.toFixed(2)} €
                      </td>

                      {/* Statut */}
                      <td className="py-3 px-4 text-center relative" style={{ minWidth: 120 }}>
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold cursor-pointer transition-transform hover:scale-105 ${statusBadgeClass(q.status)}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleStatusMenu(q.id);
                          }}
                        >
                          {t(`quote.statusOptions.${q.status}`)}
                        </span>

                        {q.showStatusMenu && (
                          <div className="absolute z-20 mt-2 left-1/2 -translate-x-1/2 bg-base-100 border border-base-200 rounded-xl shadow-xl w-40 py-1 animate-fade-in">
                            {(STATUS_TRANSITIONS[q.status] || []).length === 0 ? (
                              <div className="px-3 py-2 text-xs text-base-content/40 text-center italic">
                                Aucune transition possible
                              </div>
                            ) : (
                              (STATUS_TRANSITIONS[q.status] || []).map((status) => (
                                <div
                                  key={status}
                                  className="px-3 py-2 hover:bg-base-200 cursor-pointer transition-colors text-center text-sm text-base-content/70 hover:text-base-content"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleChangeStatus(q, status);
                                    setQuotes((prev) =>
                                      prev.map((x) => (x.id === q.id ? { ...x, showStatusMenu: false } : x))
                                    );
                                  }}
                                >
                                  {t(`quote.statusOptions.${status}`)}
                                </div>
                              ))
                            )}
                          </div>
                        )}

                        {q.status === "validated" && q.validated_at && (
                          <div
                            className="text-[10px] text-emerald-600/80 mt-1 whitespace-nowrap"
                            title={t("quote.validatedAt", "Validé le")}
                          >
                            ✓ {fmtDateTime(q.validated_at)}
                          </div>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          {/* PDF dropdown */}
                          <div className="relative">
                            <button
                              onClick={(e) => togglePdfMenu(q.id, e)}
                              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-primary/10 text-primary hover:bg-primary/20 transition-colors border border-primary/20"
                              title="Générer PDF"
                            >
                              <FaFilePdf className="text-[11px]" />
                              PDF
                              <FaChevronDown className="text-[10px]" />
                            </button>
                            {pdfMenuId === q.id && (
                              <div className="absolute right-0 mt-2 w-44 bg-base-100 border border-base-200 rounded-xl shadow-xl py-1 animate-fade-in z-20">
                                <button
                                  className="w-full text-left px-4 py-2 hover:bg-base-200 text-sm text-base-content/70 hover:text-base-content transition-colors"
                                  onClick={() => downloadPDF(q.id, "vat")}
                                >
                                  {t("quote.pdf.withVAT", "Avec TVA (TTC)")}
                                </button>
                                <button
                                  className="w-full text-left px-4 py-2 hover:bg-base-200 text-sm text-base-content/70 hover:text-base-content transition-colors"
                                  onClick={() => downloadPDF(q.id, "ht")}
                                >
                                  {t("quote.pdf.withoutVAT", "Sans TVA (HT)")}
                                </button>
                              </div>
                            )}
                          </div>

                          {/* Copier */}
                          <button
                            onClick={(e) => handleCopy(q, e)}
                            className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${
                              copyingId === q.id
                                ? "text-base-content/30 bg-base-300 cursor-wait"
                                : "text-base-content/40 hover:text-base-content hover:bg-base-300"
                            }`}
                            title={t("quote.copy", "Copier")}
                          >
                            <FaCopy className="text-xs" />
                          </button>

                          {/* Éditer */}
                          <button
                            onClick={(e) => openEdit(q, e)}
                            className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${
                              loadingEditId === q.id
                                ? "text-base-content/30 bg-base-300 cursor-wait"
                                : "text-base-content/40 hover:text-base-content hover:bg-base-300"
                            }`}
                            title={t("quote.edit")}
                          >
                            <FaEdit className="text-xs" />
                          </button>

                          {/* Supprimer */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(q.id);
                            }}
                            className="w-7 h-7 rounded-lg flex items-center justify-center text-base-content/40 hover:text-error hover:bg-error/10 transition-colors"
                            title={t("quote.delete")}
                          >
                            <FaTrash className="text-xs" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {/* Pagination serveur */}
      {pageCount > 1 && (
        <div className="flex gap-1 justify-center flex-wrap items-center">
          <button
            disabled={page === 1}
            onClick={() => onPageChange(page - 1)}
            className="px-3 py-1.5 rounded-lg border border-base-200 bg-base-100 text-base-content/70 text-xs font-medium hover:bg-base-200 disabled:opacity-40 transition-colors"
          >
            ← Préc.
          </button>
          {pageButtons.map((pg, i) => {
            const prev = pageButtons[i - 1];
            const showEllipsis = prev && pg - prev > 1;
            return (
              <React.Fragment key={pg}>
                {showEllipsis && (
                  <span className="px-1 text-base-content/30 text-xs">…</span>
                )}
                <button
                  onClick={() => onPageChange(pg)}
                  className={`w-8 h-8 rounded-lg text-xs font-semibold transition-colors ${
                    page === pg
                      ? "bg-primary text-primary-content"
                      : "border border-base-200 bg-base-100 text-base-content/70 hover:bg-base-200"
                  }`}
                >
                  {pg}
                </button>
              </React.Fragment>
            );
          })}
          <button
            disabled={page === pageCount}
            onClick={() => onPageChange(page + 1)}
            className="px-3 py-1.5 rounded-lg border border-base-200 bg-base-100 text-base-content/70 text-xs font-medium hover:bg-base-200 disabled:opacity-40 transition-colors"
          >
            Suiv. →
          </button>
          <span className="text-xs text-base-content/40 ml-2">
            Page {page}/{pageCount}
          </span>
        </div>
      )}
    </div>
  );
};

export default QuoteList;

// Format compact sans décimales pour les cartes (ex: "1 250 €").
const euroShort = (v) => `${Math.round(Number(v) || 0).toLocaleString("fr-FR")} €`;

/* ── Petites icônes SVG dédiées à la vue cartes ── */
const CalendarIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="shrink-0">
    <rect x="3" y="5" width="18" height="16" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
    <path d="M3 10h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const RouteIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" className="text-primary shrink-0">
    <circle cx="5" cy="6" r="2.5" stroke="currentColor" strokeWidth="1.8" />
    <circle cx="19" cy="18" r="2.5" stroke="currentColor" strokeWidth="1.8" />
    <path d="M5 8.5v3a4 4 0 0 0 4 4h6a4 4 0 0 1 4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeDasharray="2 3" />
  </svg>
);

const CardIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="shrink-0">
    <rect x="2.5" y="5" width="19" height="14" rx="2.2" stroke="currentColor" strokeWidth="1.8" />
    <path d="M2.5 9.5h19" stroke="currentColor" strokeWidth="1.8" />
    <path d="M6 14.5h5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const TruckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="shrink-0">
    <path d="M2 7h11v9H2z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    <path d="M13 10h4l4 3.2V16h-8z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    <circle cx="6.5" cy="17.5" r="1.8" fill="currentColor" />
    <circle cx="17" cy="17.5" r="1.8" fill="currentColor" />
  </svg>
);

const HouseIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="shrink-0">
    <path d="M3 11l9-7 9 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M5 10v9a1 1 0 0 0 1 1h4v-6h4v6h4a1 1 0 0 0 1-1v-9" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
  </svg>
);
